import addYears from 'date-fns/addYears';
import format from 'date-fns/format';
import { ExperienceSource } from 'model/IExperience';
import withlocalsOrganizationSchema from 'schemas/withlocalsOrganization';
import { generatePathnameWithLangCode } from 'utils/RouteHelper';
import cleanString from 'utils/cleanString';
import { useI18n } from '~/stores/i18n';

export interface ReviewForSchema {
  id: string;
  guest_name: string;
  created: string;
  comment: string;
  title: string;
  rating: number;
}

export default function productSchema(
  experience: ExperienceSource,
  reviews?: ReviewForSchema[],
): string {
  const title = cleanString(experience.title);
  const teaser = cleanString(experience.teaser);

  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: title,
    image: experience.mainPhoto,
    description: teaser,
    productID: `withlocals:experience:${experience.id}`,
    identifier: `withlocals:experience:${experience.id}`,
    mpn: `withlocals:experience:${experience.id}`,
    sku: experience.id,
    url: `${URL_HOST}${generatePathnameWithLangCode(
      `/experience/${experience.pretty_url}/`,
      useI18n.getState().lang,
    )}`,
    offers: {
      '@type': 'Offer',
      availability: 'http://schema.org/OnlineOnly',
      price: experience.pricing.advertised_price_origin.amount.value,
      priceCurrency: 'EUR',
      url: `${URL_HOST}${generatePathnameWithLangCode(
        `/experience/${experience.pretty_url}/`,
        useI18n.getState().lang,
      )}`,
      priceValidUntil: format(addYears(new Date(), 1), 'yyyy-MM-dd'),
    },
    brand: withlocalsOrganizationSchema(),
  };

  if (experience.aggregates.reviews_count > 0) {
    productSchema['aggregateRating'] = {
      '@type': 'AggregateRating',
      ratingValue: Math.min(Number(experience.aggregates.experience_rating), 5),
      bestRating: 5,
      worstRating: 1,
      reviewCount: experience.aggregates.reviews_count,
    };
  }

  if (reviews && reviews.length > 0) {
    productSchema['review'] = reviews.slice(0, 10).map((review) => ({
      '@type': 'Review',
      author: {
        '@type': 'Person',
        name: review.guest_name?.split('@')[0] || 'Guest',
      },
      datePublished: review.created
        ? format(new Date(review.created), 'yyyy-MM-dd')
        : undefined,
      reviewBody: cleanString(review.comment || ''),
      name: cleanString(review.title || ''),
      reviewRating: {
        '@type': 'Rating',
        ratingValue: review.rating,
        bestRating: 5,
        worstRating: 1,
      },
    }));
  }

  return JSON.stringify(productSchema);
}
